import type { ColumnDef } from '@tanstack/react-table';
import {
  createSerialColumn,
  getAreaColumn,
  MemberNameCell,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { InstituteNumbers } from '@/components/common/InstituteNumbers';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { MpRegionDonationRow } from '../types/marketPromotion.types';

export const REGION_DONATION_ZM_COLUMN_ID = 'name';

export function getRegionDonationColumns(
  searchText: string | undefined,
): ColumnDef<MpRegionDonationRow, unknown>[] {
  const highlight = (text: string | null) =>
    text === null ? <EmptyValue /> : <HighlightText text={text} highlightedText={searchText} />;

  return [
    createSerialColumn<MpRegionDonationRow>(),
    {
      id: REGION_DONATION_ZM_COLUMN_ID,
      header: 'ZM Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: row.original.name,
            hrEmpId: row.original.hrEmpId,
            mobile: row.original.userMobile,
          }}
          searchText={searchText}
        />
      ),
    },
    {
      accessorKey: 'promoItemName',
      header: 'Donation Project',
      cell: ({ row }) => <WrappingCellText>{row.original.promoItemName}</WrappingCellText>,
    },
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlight(row.original.instituteName)}
          primaryClassName="font-medium"
          secondary={
            <InstituteNumbers
              code={row.original.instituteCode}
              eiin={row.original.instituteEiin}
              highlightedText={searchText}
            />
          }
        />
      ),
    },
    {
      id: 'teacher',
      header: 'Teacher',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlight(row.original.teacherName)}
          secondary={highlight(row.original.teacherMobile)}
        />
      ),
    },
    getAreaColumn<MpRegionDonationRow>(),
    {
      accessorKey: 'dcrDate',
      header: 'DCR Date',
      cell: ({ row }) => formatDisplayDate(row.original.dcrDate),
    },
    {
      accessorKey: 'distributedAmount',
      header: 'Amount (Tk)',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.distributedAmount)}</span>
      ),
    },
  ];
}
